import { useState, useEffect, useRef, Fragment } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import useSEO from "../hooks/useSEO";
import { useCart } from "../context/CartContext";
import { useToast, QtyControl, COLOR_MAP } from "../components/Navbar";

const API = process.env.REACT_APP_API_URL || "http://localhost:5001";

// ── Helpers ───────────────────────────────────────────────────────────────────
function useScrollReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function Stars({ rating }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1,2,3,4,5].map(n => (
        <svg key={n} className={`w-3 h-3 ${n <= Math.round(rating) ? "fill-amber-400" : "fill-stone-200"}`} viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
      <span className="text-[11px] text-stone-400 ml-1">{rating}</span>
    </div>
  );
}

const TAG_STYLES = {
  Premium:    "bg-amber-100 text-amber-700",
  Bestseller: "bg-rose-100 text-rose-700",
  Limited:    "bg-purple-100 text-purple-700",
  New:        "bg-emerald-100 text-emerald-700",
  Trending:   "bg-sky-100 text-sky-700",
};

// ── Product Card (full featured like Shop page) ───────────────────────────────
function ProductCard({ product, index, visible }) {
  const { addToCart, isInCart, getQuantity, updateQuantity } = useCart();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [wishlisted, setWishlisted] = useState(false);
  const [btnKey, setBtnKey] = useState(0);

  const inCart = isInCart(product.id);
  const qty    = getQuantity(product.id);

  const images = typeof product.images === "string" ? JSON.parse(product.images) : product.images;
  const imgList = Array.isArray(images) ? images.filter(Boolean) : [];
  const [imgIndex, setImgIndex] = useState(0);
  const imgSrc = imgList.length > 0 ? imgList[imgIndex] : "";
  const colorList = (() => {
    const colors = typeof product.colors === "string" ? JSON.parse(product.colors) : product.colors;
    return Array.isArray(colors) ? colors : [];
  })();
  const MAX_COLOR_DOTS = 5;
  const visibleColors = colorList.slice(0, MAX_COLOR_DOTS);
  const extraColorCount = colorList.length - visibleColors.length;
  const displayPrice = product.sale_price || product.price;

  const handleAdd = (e) => {
    e.stopPropagation();
    addToCart(product.id, 1);
    addToast(`${product.name} added to cart`);
    setBtnKey(k => k + 1);
  };

  const handleImageClick = (e) => {
  if (imgList.length <= 1) return;
  e.stopPropagation();
  setImgIndex(i => (i + 1) % imgList.length);
};

const handleDotClick = (e, i) => {
  e.stopPropagation();
  setImgIndex(i);
};

  return (
    <div
      onClick={() => {
  // Save both absolute position and percentage
  const scrollY = window.scrollY;
  const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
  const percentage = maxScroll > 0 ? scrollY / maxScroll : 0;
  
  sessionStorage.setItem('homeScrollData', JSON.stringify({
    y: scrollY,
    percentage: percentage
  }));
  navigate(`/product/${product.id}`);
}}
      className={`group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-1.5 border border-stone-100 flex flex-col cursor-pointer ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
      style={{ transitionDelay: `${(index % 8) * 60}ms` }}
    >
      <div className="relative aspect-[3/4] overflow-hidden bg-stone-100">
        <img
  src={imgSrc}
  alt={product.name}
  onClick={handleImageClick}
  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
  loading="lazy"
/>
<div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/8 transition-colors duration-300" />
{imgList.length > 1 && (
  <div className={`absolute left-0 right-0 flex items-center justify-center gap-1.5 z-10 transition-all duration-300 ${inCart ? "bottom-14" : "bottom-2.5"}`}>
    {imgList.map((_, i) => (
      <button
        key={i}
        onClick={(e) => handleDotClick(e, i)}
        aria-label={`Show image ${i + 1}`}
        className={`h-1.5 rounded-full transition-all duration-300 ${
          i === imgIndex ? "w-4 bg-white shadow" : "w-1.5 bg-white/60 hover:bg-white/85"
        }`}
      />
    ))}
  </div>
)}
        {product.tag && (
          <span className={`absolute top-3 left-3 text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide ${TAG_STYLES[product.tag] ?? "bg-stone-100 text-stone-600"}`}>
            {product.tag}
          </span>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); setWishlisted(w => !w); }}
          className={`absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center shadow-md transition-all duration-300 ${wishlisted ? "bg-rose-500 text-white scale-110" : "bg-white/90 text-stone-400 opacity-0 group-hover:opacity-100 hover:text-rose-500"}`}
        >
          <svg className="w-4 h-4" fill={wishlisted ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
        </button>
        {inCart && (
          <div className="absolute bottom-3 left-3 right-3" onClick={e => e.stopPropagation()}>
            <div className="bg-stone-900/85 backdrop-blur-sm rounded-xl px-3 py-1.5 flex items-center justify-between">
              <span className="text-white text-xs font-semibold">In cart</span>
              <QtyControl variant="dark" value={qty} onChange={(newQty) => updateQuantity(product.id, newQty)} />
            </div>
          </div>
        )}
      </div>
      <div className="p-4 flex flex-col flex-1">
        <span className="text-[10px] font-semibold tracking-widest uppercase text-stone-400 mb-1">{product.category}</span>
        <h3 className="text-base font-semibold text-stone-900 mb-1.5 leading-snug" style={{ fontFamily: "'Playfair Display', serif" }}>{product.name}</h3>
        <Stars rating={product.rating || 4.5} />
        {visibleColors.length > 0 && (
          <div className="flex items-center mt-2 -space-x-1">
            {visibleColors.map((c, i) => (
              <span key={c.name ?? i} title={c.name}
                className="w-3.5 h-3.5 rounded-full border-2 border-white ring-1 ring-stone-200 flex-shrink-0"
                style={{ backgroundColor: c.hex ?? COLOR_MAP[c.name] ?? "#ccc" }} />
            ))}
            {extraColorCount > 0 && (
              <span className="text-[9px] font-semibold text-stone-400 pl-2">+{extraColorCount}</span>
            )}
          </div>
        )}
        <div className="flex items-center justify-between mt-auto pt-3 gap-2">
          <div className="flex items-center gap-1.5 flex-shrink-0 min-w-0">
            <span className="text-lg font-bold text-stone-900 truncate" style={{ fontFamily: "'Playfair Display', serif" }}>${displayPrice}</span>
            {product.sale_price && product.price > product.sale_price && (
              <span className="text-xs text-stone-400 line-through flex-shrink-0">${product.price}</span>
            )}
          </div>
          <button
            key={btnKey}
            onClick={handleAdd}
            className={`flex items-center justify-center gap-1.5 font-semibold rounded-full transition-all duration-200 active:scale-95 flex-shrink-0 px-2.5 py-2 sm:px-3.5 text-xs ${inCart ? "bg-emerald-500 text-white hover:bg-emerald-600" : "bg-stone-900 text-white hover:bg-rose-500"}`}
          >
            {inCart ? (
              <>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                <span className="hidden sm:inline">Add More</span>
              </>
            ) : (
              <>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4" /><circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /></svg>
                <span className="hidden sm:inline">Add to Cart</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Hero ──────────────────────────────────────────────────────────────────────
function Hero() {
  const [loaded, setLoaded] = useState(false);
  const navigate = useNavigate();
  const year = new Date().getFullYear();
  useEffect(() => { setTimeout(() => setLoaded(true), 100); }, []);

  return (
    <section className="relative h-screen min-h-[680px] flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img src="https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=1600&q=85" alt="Hero" className="w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-950/70 via-stone-900/40 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-stone-950/40 via-transparent to-transparent" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 w-full">
        <div className={`max-w-xl transition-all duration-1000 ${loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-rose-400 animate-pulse" />
            <span className="text-white/90 text-xs font-medium tracking-widest uppercase">New Collection {year}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-[1.05] mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
            Elevate<br />
            <em className="not-italic text-transparent bg-clip-text bg-gradient-to-r from-rose-300 to-amber-300">Your Style</em>
          </h1>
          <p className="text-sm sm:text-base md:text-lg text-white/75 font-light leading-relaxed mb-8 md:mb-10 max-w-sm">
            Discover premium clothing &amp; jewelry crafted for the modern woman who refuses to compromise.
          </p>
          <div className="flex flex-wrap gap-4">
            <button onClick={() => {
  const scrollY = window.scrollY;
  const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
  const percentage = maxScroll > 0 ? scrollY / maxScroll : 0;
  sessionStorage.setItem('homeScrollData', JSON.stringify({ y: scrollY, percentage }));
  navigate("/shop");
}}
  className="group flex items-center gap-2.5 bg-white text-stone-900 font-semibold px-8 py-4 rounded-full hover:bg-rose-500 hover:text-white transition-all duration-300 shadow-xl hover:-translate-y-0.5">
  Shop Now
  <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
  </svg>
</button>
            <button onClick={() => {
  const scrollY = window.scrollY;
  const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
  const percentage = maxScroll > 0 ? scrollY / maxScroll : 0;
  sessionStorage.setItem('homeScrollData', JSON.stringify({ y: scrollY, percentage }));
  navigate("/shop?category=Jewelry");
}}
  className="font-medium text-white border border-white/30 px-8 py-4 rounded-full hover:bg-white/10 backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5">
  Explore Jewelry
</button>
          </div>
          <div className={`flex gap-5 md:gap-8 mt-8 md:mt-12 pt-6 md:pt-8 border-t border-white/15 transition-all duration-1000 delay-500 ${loaded ? "opacity-100" : "opacity-0"}`}>
            {[["2,400+", "Styles Sold In-Store"], ["15,000+", "Customers Served Since Opening"], ["4.9★", "Average Customer Rating"]].map(([num, label]) => (
              <div key={label}>
                <div className="text-lg md:text-2xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>{num}</div>
                <div className="text-[10px] md:text-xs text-white/55 font-medium uppercase tracking-wider mt-0.5">{label}</div>
              </div>
            ))}
          </div>
          <p className="text-white/50 text-xs md:text-sm mt-4 max-w-md">
            Serving customers through our New York retail store for years—now bringing our collection online.
          </p>
        </div>
      </div>
    </section>
  );
}

// ── Category Nav Strip ────────────────────────────────────────────────────────
function CategoryStrip({ activeCategory, onSelect }) {
  const CATS = [
    { label: "All",                      key: "all" },
    { label: "Indian Traditional",        key: "Indian Traditional Dresses" },
    { label: "Jewelry",                  key: "Jewelry" },
    { label: "Summer Wear",              key: "Summer Wear" },
    { label: "Winter Wear",              key: "Winter Wear" },
    // { label: "Cosmetics",               key: "Cosmetics" },
  ];
  return (
    <div className="sticky top-16 z-20 bg-white border-b border-stone-100 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex gap-2 overflow-x-auto py-3" style={{ scrollbarWidth: "none" }}>
          {CATS.map(cat => (
            <button key={cat.key} onClick={() => onSelect(cat.key)}
              className={`whitespace-nowrap px-5 py-2 rounded-full text-xs font-semibold transition-all duration-200 flex-shrink-0 ${
                activeCategory === cat.key ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"
              }`}>
              {cat.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Category Section ──────────────────────────────────────────────────────────
function CategorySection({ category, products, bg }) {
  const [ref, visible] = useScrollReveal();
  const navigate = useNavigate();
  const [showAll, setShowAll] = useState(false);

  const CAT_CONFIG = {
    "Indian Traditional Dresses": { subtitle: "Ethnic Wear", link: "/shop?category=Indian Traditional Dresses", color: "from-rose-900/60 to-stone-900/80" },
    "Jewelry":    { subtitle: "Accessories",  link: "/shop?category=Jewelry",    color: "from-amber-900/60 to-stone-900/80" },
    "Summer Wear":{ subtitle: "Season Edit",  link: "/shop?category=Summer Wear",color: "from-orange-900/60 to-stone-900/80" },
    "Winter Wear":{ subtitle: "Cozy Picks",   link: "/shop?category=Winter Wear",color: "from-slate-900/60 to-stone-900/80" },
    // "Cosmetics":  { subtitle: "Beauty & Care",link: "/cosmetics",                color: "from-pink-900/60 to-stone-900/80" },
  };

  const config = CAT_CONFIG[category] || { subtitle: "Collection", link: "/shop" };
  const displayProducts = showAll ? products : products.slice(0, 8);

  if (!products || products.length === 0) return null;

  return (
    <section ref={ref} className={`py-16 ${bg}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className={`flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div>
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-rose-500 mb-2 block">{config.subtitle}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>{category}</h2>
            <p className="text-stone-400 text-sm mt-1">{products.length} products</p>
          </div>
          <button onClick={() => {
  const scrollY = window.scrollY;
  const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
  const percentage = maxScroll > 0 ? scrollY / maxScroll : 0;
  sessionStorage.setItem('homeScrollData', JSON.stringify({ y: scrollY, percentage }));
  navigate(config.link);
}}
  className="self-start md:self-auto text-sm font-semibold text-stone-700 border border-stone-200 px-6 py-2.5 rounded-full hover:bg-rose-500 hover:text-white hover:border-rose-500 transition-all duration-300">
  View All →
</button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {displayProducts.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} visible={visible} />
          ))}
        </div>

        {products.length > 8 && (
          <div className="flex justify-center mt-10">
            <button
  onClick={() => {
  if (showAll) {
    const scrollY = window.scrollY;
    const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
    const percentage = maxScroll > 0 ? scrollY / maxScroll : 0;
    sessionStorage.setItem('homeScrollData', JSON.stringify({ y: scrollY, percentage }));
    navigate(config.link);
  } else {
    setShowAll(true);
  }
}}
  className="flex items-center gap-2 px-8 py-3 rounded-full border-2 border-stone-200 text-stone-700 font-semibold text-sm hover:border-rose-500 hover:text-rose-500 transition-all duration-300"
>
  {showAll ? `Go to ${category} Page →` : `Show All ${products.length} Products`}
</button>
          </div>
        )}
      </div>
    </section>
  );
}

// ── Testimonials ──────────────────────────────────────────────────────────────
const TESTIMONIALS = [
  { name: "Sophia Carter", location: "New York, NY", rating: 5, text: "Absolutely obsessed with my Gold Leaf Necklace. The craftsmanship is stunning and it arrived beautifully packaged.", initials: "SC", color: "bg-rose-100 text-rose-700" },
  { name: "Emily Rhodes",  location: "Los Angeles, CA", rating: 5, text: "The Silk Wrap Dress fits like it was made for me. Premium quality fabric, elegant cut — I've received so many compliments.", initials: "ER", color: "bg-amber-100 text-amber-700" },
  { name: "Natalie Brooks", location: "Chicago, IL", rating: 5, text: "Fast shipping, gorgeous packaging, and the Pearl Drop Earrings are even more beautiful in person.", initials: "NB", color: "bg-slate-100 text-slate-700" },
];

function Testimonials() {
  const [ref, visible] = useScrollReveal();
  return (
    <section ref={ref} className="py-24 bg-stone-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className={`text-center mb-14 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-rose-500 mb-3 block">Reviews</span>
          <h2 className="text-4xl md:text-5xl font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>What Our Clients Say</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <div key={t.name}
              className={`bg-white rounded-2xl p-7 shadow-sm hover:shadow-lg transition-all duration-500 hover:-translate-y-1 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
              style={{ transitionDelay: `${i * 120}ms` }}>
              <div className="text-4xl text-rose-200 font-serif leading-none mb-4">"</div>
              <p className="text-stone-600 text-sm leading-relaxed mb-6">{t.text}</p>
              <div className="flex items-center gap-3 pt-5 border-t border-stone-100">
                <div className={`w-10 h-10 rounded-full ${t.color} flex items-center justify-center font-bold text-sm flex-shrink-0`}>{t.initials}</div>
                <div>
                  <div className="font-semibold text-stone-800 text-sm">{t.name}</div>
                  <div className="text-stone-400 text-xs mb-1">{t.location}</div>
                  <div className="flex gap-0.5">{[1,2,3,4,5].map(n => <svg key={n} className="w-3 h-3 fill-amber-400" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Newsletter ────────────────────────────────────────────────────────────────
function Newsletter() {
  const [ref, visible] = useScrollReveal();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const handleSubmit = () => {
    if (email.includes("@")) { setSent(true); setEmail(""); setTimeout(() => setSent(false), 3000); }
  };
  return (
    <section ref={ref} className="py-20 bg-white">
      <div className="max-w-2xl mx-auto px-6 text-center">
        <div className={`transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-rose-500 mb-3 block">Stay Updated</span>
          <h2 className="text-4xl md:text-5xl font-bold text-stone-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Join the Inner Circle</h2>
          <p className="text-stone-500 mb-10 leading-relaxed">Subscribe for early access to new collections, exclusive member discounts, and style inspiration delivered weekly.</p>
          <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleSubmit()}
              placeholder="Your email address"
              className="flex-1 px-5 py-4 rounded-2xl border border-stone-200 bg-stone-50 text-stone-800 text-sm placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-rose-400 transition-all duration-300"
            />
            <button onClick={handleSubmit}
              className={`px-7 py-4 rounded-2xl font-semibold text-sm transition-all duration-300 ${sent ? "bg-green-500 text-white" : "bg-stone-900 text-white hover:bg-rose-500 hover:-translate-y-0.5"}`}>
              {sent ? "✓ Subscribed!" : "Subscribe"}
            </button>
          </div>
          <p className="text-stone-400 text-xs mt-4">No spam. Unsubscribe anytime. ✦</p>
        </div>
      </div>
    </section>
  );
}

function TrustStrip() {
  const TRUST = [
    {
      label: "Free NYC Delivery",
      sub: "Orders over $100",
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" /></svg>,
    },
    {
      label: "Same-Day Dispatch",
      sub: "Order by 2 PM weekdays",
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" /></svg>,
    },
    {
      label: "Easy Returns",
      sub: "30-day no-questions policy",
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" /></svg>,
    },
    {
      label: "Premium Curated",
      sub: "Hand-picked for NY style",
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" /></svg>,
    },
    {
      label: "Secure Checkout",
      sub: "SSL encrypted & safe",
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" /></svg>,
    },
  ];

  return (
    <section className="bg-white border-b border-stone-100">
      {/* Mobile: horizontal scroll. Desktop: even grid */}
      <div
        className="flex md:grid md:grid-cols-5 overflow-x-auto md:overflow-visible divide-x divide-stone-100"
        style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}
      >
        {TRUST.map(({ label, sub, icon }) => (
          <div
            key={label}
            className="flex items-center gap-2.5 px-4 md:px-5 py-3.5 flex-shrink-0 md:flex-shrink min-h-[52px]"
          >
            <span className="text-rose-400 flex-shrink-0">{icon}</span>
            <div className="whitespace-nowrap md:whitespace-normal">
              <p className="text-xs font-semibold text-stone-800 leading-tight">{label}</p>
              <p className="text-[11px] text-stone-400 leading-tight mt-0.5">{sub}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function PromoBanner() {
  const navigate = useNavigate();
  const [discount, setDiscount] = useState(undefined);
  const [discountLoaded, setDiscountLoaded] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/admin/discounts/active`, {
      headers: { "Cache-Control": "no-cache" },
    })
      .then(r => r.json())
      .then(d => { setDiscount(d?.empty ? null : d); setDiscountLoaded(true); })
      .catch(() => setDiscountLoaded(true));
  }, []);

  if (!discountLoaded || !discount) return null;

  const discountLabel = discount.type === "percentage"
    ? `${discount.value}% Off`
    : `$${discount.value} Off`;

  const handleCopy = () => {
    navigator.clipboard?.writeText(discount.code).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <section className="py-4 px-4 md:py-8 md:px-6">
      <div className="max-w-7xl mx-auto">
        <div className="relative overflow-hidden rounded-2xl md:rounded-3xl bg-stone-950 px-5 py-6 md:px-16 md:py-16">

          {/* Subtle glow blobs — decorative only */}
          <div className="absolute top-0 right-0 w-48 h-48 rounded-full opacity-10"
            style={{ background: "radial-gradient(circle, #f43f5e 0%, transparent 70%)", transform: "translate(30%, -30%)" }} />
          <div className="absolute bottom-0 left-0 w-36 h-36 rounded-full opacity-[0.06]"
            style={{ background: "radial-gradient(circle, #fbbf24 0%, transparent 70%)", transform: "translate(-30%, 30%)" }} />

          <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">

            {/* Left: text */}
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 bg-rose-500/15 border border-rose-400/25 text-rose-400 text-[10px] font-bold px-3 py-1 rounded-full mb-3 uppercase tracking-widest">
                <span className="w-1.5 h-1.5 rounded-full bg-rose-400 animate-pulse" />
                Limited Time
              </div>

              <h2 className="text-3xl md:text-5xl font-bold text-white leading-tight mb-2"
                style={{ fontFamily: "'Playfair Display', serif" }}>
                Flat{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-400 to-amber-400">
                  {discountLabel}
                </span>
                {" "}Storewide
              </h2>

              <p className="text-white/50 text-sm md:text-base font-light">
                New York's favourite fashion store — your discount is waiting.
              </p>

              {discount.expires_at && (
                <p className="text-white/30 text-xs mt-1.5">
                  Expires {new Date(discount.expires_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                </p>
              )}
            </div>

            {/* Right: code + CTA */}
            <div className="flex flex-col gap-3 md:items-end w-full md:w-auto">
              {/* Code pill — tap to copy on mobile */}
              <button
                onClick={handleCopy}
                className="w-full md:w-auto flex items-center justify-between gap-4 bg-white/[0.06] border border-dashed border-white/20 rounded-xl px-5 py-3.5 min-h-[52px] active:bg-white/10 transition-colors"
              >
                <span className="text-amber-300 font-bold text-lg tracking-widest">
                  {discount.code}
                </span>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-md transition-all ${
                  copied
                    ? "bg-emerald-500/20 text-emerald-400"
                    : "bg-white/10 text-white/50"
                }`}>
                  {copied ? "✓ Copied!" : "Tap to copy"}
                </span>
              </button>

              {/* CTA button */}
              <button
  onClick={() => {
    const scrollY = window.scrollY;
    const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
    const percentage = maxScroll > 0 ? scrollY / maxScroll : 0;
    sessionStorage.setItem('homeScrollData', JSON.stringify({ y: scrollY, percentage }));
    navigate("/shop");
  }}
  className="w-full md:w-auto flex items-center justify-center gap-2 bg-rose-500 hover:bg-rose-400 active:scale-[0.98] text-white font-semibold text-sm px-8 py-4 rounded-xl min-h-[52px] transition-all duration-200"
>
  Shop Now & Save
  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
  </svg>
</button>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}

// ── CATEGORIES CONFIG ─────────────────────────────────────────────────────────
const CATEGORY_ORDER = [
  { key: "Indian Traditional Dresses", bg: "bg-white" },
  { key: "Jewelry",                    bg: "bg-stone-50" },
  { key: "Summer Wear",                bg: "bg-white" },
  { key: "Winter Wear",                bg: "bg-stone-50" },
  { key: "Cosmetics",                  bg: "bg-white" },
];

// ── Page Export ───────────────────────────────────────────────────────────────
export default function Home() {
  const seo = useSEO("home");
  const [allProducts,      setAllProducts]      = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [activeCategory,   setActiveCategory]   = useState("all");

 useEffect(() => {
  const savedScrollData = sessionStorage.getItem('homeScrollData');
  let savedY = null;
  let savedPercentage = null;
  
  if (savedScrollData) {
    try {
      const data = JSON.parse(savedScrollData);
      savedY = data.y;
      savedPercentage = data.percentage;
    } catch (e) {
      // If old format, try parsing as number
      savedY = parseInt(savedScrollData, 10);
    }
  }

  // If we have a percentage, restore based on that first
  // (this will work even during loading)
  if (savedPercentage !== null && !isNaN(savedPercentage) && savedPercentage >= 0 && savedPercentage <= 1) {
    const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
    window.scrollTo({ top: maxScroll * savedPercentage, behavior: 'instant' });
  } else if (savedY !== null && !isNaN(savedY)) {
    window.scrollTo({ top: savedY, behavior: 'instant' });
  }

  fetch(`${API}/api/products`)
    .then(r => r.json())
    .then(data => {
      setAllProducts(Array.isArray(data) ? data : []);
      setLoading(false);
      
      // Final correction after content loads
      if (savedScrollData) {
        // Use multiple attempts with increasing delays
        const restoreScroll = (attempt = 0) => {
          const maxDelay = 500; // milliseconds
          const delay = Math.min(50 + attempt * 50, maxDelay);
          
          setTimeout(() => {
            if (savedPercentage !== null && !isNaN(savedPercentage) && savedPercentage >= 0 && savedPercentage <= 1) {
              const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
              const targetY = maxScroll * savedPercentage;
              window.scrollTo({ top: targetY, behavior: 'instant' });
              
              // Check if we're at the target position (within 50px)
              const currentY = window.scrollY;
              if (Math.abs(currentY - targetY) > 50 && attempt < 10) {
                restoreScroll(attempt + 1);
              } else if (attempt >= 10 || Math.abs(currentY - targetY) <= 50) {
                sessionStorage.removeItem('homeScrollData');
              }
            } else if (savedY !== null && !isNaN(savedY)) {
              window.scrollTo({ top: savedY, behavior: 'instant' });
              
              const currentY = window.scrollY;
              if (Math.abs(currentY - savedY) > 50 && attempt < 10) {
                restoreScroll(attempt + 1);
              } else if (attempt >= 10 || Math.abs(currentY - savedY) <= 50) {
                sessionStorage.removeItem('homeScrollData');
              }
            }
          }, delay);
        };
        
        // Start the correction process
        restoreScroll(0);
      }
    })
    .catch(() => setLoading(false));
}, []);

  // Group products by category
  const byCategory = {};
  allProducts.forEach(p => {
    if (!byCategory[p.category]) byCategory[p.category] = [];
    byCategory[p.category].push(p);
  });

  // Filter based on active category tab
  const categoriesToShow = activeCategory === "all"
    ? CATEGORY_ORDER
    : CATEGORY_ORDER.filter(c => c.key === activeCategory);

  return (
    <>
      <Helmet>
        <title>{seo?.title || "Sagar Lifestyle NY LLC — Premium Clothing & Jewelry"}</title>
        <meta name="description" content={seo?.description || "Discover premium clothing and jewelry."} />
        <meta name="keywords"    content={seo?.keywords    || "sagar fashion, clothing, jewelry, cosmetics"} />
      </Helmet>

      <Hero />
      <TrustStrip />

      <CategoryStrip activeCategory={activeCategory} onSelect={setActiveCategory} />

      {loading ? (
        <div className="flex justify-center py-32">
          <div className="w-10 h-10 border-2 border-stone-200 border-t-rose-400 rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {categoriesToShow.map(({ key, bg }, idx) => (
            <Fragment key={key}>
              <CategorySection
                category={key}
                products={byCategory[key] || []}
                bg={bg}
              />
              {idx === 0 && activeCategory === "all" && <PromoBanner />}
            </Fragment>
          ))}
        </>
      )}

      <Testimonials />
      <Newsletter />
    </>
  );
}